import type { MediaclipPendingStatus, MediaclipUploadedPhoto, MediaclipUploadSession } from '@/types/DirectUpload';

/**
 * Mediaclip's `remaining` is a poll-again hint, not time left: sleeps derived from it are clamped.
 * Each status request already costs a few hundred milliseconds, so the sleep between polls stays short.
 */
const MIN_SLEEP_MS = 100;
const MAX_SLEEP_MS = 250;
const DEFAULT_SLEEP_MS = 250;

/** Big photos legitimately take longer to process, so the wait's budget grows with the file. */
const PROCESSING_TIMEOUT_BASE_MS = 30_000;
const PROCESSING_TIMEOUT_PER_MEGABYTE_MS = 1000;
const PROCESSING_TIMEOUT_MAX_MS = 120_000;
const BYTES_PER_MEGABYTE = 1024 * 1024;

const ABORT_CHECK_MS = 100;

/** A status check that hangs or fails transiently must not sink an upload whose photo is fine. */
const STATUS_REQUEST_TIMEOUT_MS = 5000;
const MAX_CONSECUTIVE_STATUS_FAILURES = 3;

/** Checked before the transfer so an unacceptable photo never leaves the browser. */
const ALLOWED_PHOTO_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'];
const MAX_PHOTO_SIZE_BYTES = 100 * 1024 * 1024;

const TRANSLATION_PREFIX = 'pages.product_detail_page.product_info.product_button.upload.errors';

const FIRST_SUCCESS_STATUS = 200;
const FIRST_NON_SUCCESS_STATUS = 300;
const HTTP_REQUEST_TIMEOUT = 408;
const HTTP_UNPROCESSABLE_ENTITY = 422;
const HTTP_TOO_MANY_REQUESTS = 429;
const FIRST_SERVER_ERROR_STATUS = 500;

export class DirectUploadError extends Error {
    public constructor(
        message: string,
        public readonly status?: number,
        /** Translated by the server, safe to show the customer. */
        public readonly customerMessage?: string,
    ) {
        super(message);
        this.name = 'DirectUploadError';
    }
}

/**
 * Translation key for why the photo is unacceptable, or undefined when it may be uploaded.
 * Checked before the transfer so an oversized photo costs no bandwidth.
 */
export function photoRejectionKey(file: File): string | undefined {
    const extension = file.name.split('.').pop()?.toLowerCase();

    if (!extension || !ALLOWED_PHOTO_EXTENSIONS.includes(extension)) {
        return `${TRANSLATION_PREFIX}.invalid_file_type`;
    }

    if (file.size > MAX_PHOTO_SIZE_BYTES) {
        return `${TRANSLATION_PREFIX}.file_too_large`;
    }

    return undefined;
}

function clampSleepMs(milliseconds: number | undefined): number {
    if (typeof milliseconds !== 'number' || Number.isNaN(milliseconds)) {
        return DEFAULT_SLEEP_MS;
    }

    return Math.max(MIN_SLEEP_MS, Math.min(milliseconds, MAX_SLEEP_MS));
}

function processingTimeoutMs(fileSizeBytes: number): number {
    const scaledTimeout = PROCESSING_TIMEOUT_BASE_MS + (fileSizeBytes / BYTES_PER_MEGABYTE) * PROCESSING_TIMEOUT_PER_MEGABYTE_MS;

    return Math.min(scaledTimeout, PROCESSING_TIMEOUT_MAX_MS);
}

function sleep(milliseconds: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, milliseconds));
}

/** Sleeps in short steps so an abort is honoured promptly mid-wait. */
async function abortableSleep(durationMs: number, signal: AbortSignal | undefined): Promise<void> {
    const wakeAt = Date.now() + durationMs;

    while (Date.now() < wakeAt) {
        await sleep(Math.min(ABORT_CHECK_MS, wakeAt - Date.now()));

        if (signal?.aborted) {
            return;
        }
    }
}

function authorizationHeader(session: MediaclipUploadSession): string {
    return `${session.scheme} ${session.token}`;
}

function uploadEndpoint(session: MediaclipUploadSession, file: File): string {
    return (
        `${session.upload_url}/stores/${encodeURIComponent(session.store_key)}` +
        `/users/${encodeURIComponent(session.user_id)}/sources/uploads/photos` +
        `?async=true&originalFileName=${encodeURIComponent(file.name)}`
    );
}

function isSuccessStatus(status: number): boolean {
    return status >= FIRST_SUCCESS_STATUS && status < FIRST_NON_SUCCESS_STATUS;
}

function openUploadRequest(file: File, session: MediaclipUploadSession): XMLHttpRequest {
    const request = new XMLHttpRequest();

    request.open('POST', uploadEndpoint(session, file), true);
    request.setRequestHeader('Authorization', authorizationHeader(session));
    request.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    return request;
}

function trackTransferProgress(request: XMLHttpRequest, onProgress: (percentage: number) => void): void {
    request.upload.onprogress = (event: ProgressEvent): void => {
        if (!event.lengthComputable) {
            return;
        }

        onProgress(Math.floor((event.loaded / event.total) * 100));
    };
}

function abortedError(): DirectUploadError {
    return new DirectUploadError('Upload aborted');
}

function throwIfAborted(signal: AbortSignal | undefined): void {
    if (signal?.aborted) {
        throw abortedError();
    }
}

function abortRequestWith(request: XMLHttpRequest, signal: AbortSignal | undefined): void {
    signal?.addEventListener('abort', () => request.abort(), { once: true });
}

function uploadedPhotoFrom(request: XMLHttpRequest): MediaclipUploadedPhoto {
    if (!isSuccessStatus(request.status)) {
        throw new DirectUploadError(`Mediaclip upload failed (${request.status})`, request.status);
    }

    try {
        return JSON.parse(request.responseText) as MediaclipUploadedPhoto;
    } catch (_error: unknown) {
        throw new DirectUploadError('Mediaclip returned an unreadable upload response');
    }
}

function settleOnCompletion(request: XMLHttpRequest, resolve: (photo: MediaclipUploadedPhoto) => void, reject: (error: unknown) => void): void {
    request.onload = (): void => {
        try {
            resolve(uploadedPhotoFrom(request));
        } catch (error: unknown) {
            reject(error);
        }
    };
    request.onerror = (): void => reject(new DirectUploadError('Network error while uploading to Mediaclip'));
    request.onabort = (): void => reject(abortedError());
}

/** XHR rather than fetch: only XHR reports bytes sent, which is what drives the bar. */
export function uploadToMediaclip(
    file: File,
    session: MediaclipUploadSession,
    onProgress: (percentage: number) => void,
    signal?: AbortSignal,
): Promise<MediaclipUploadedPhoto> {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(abortedError());

            return;
        }

        const request = openUploadRequest(file, session);
        trackTransferProgress(request, onProgress);
        abortRequestWith(request, signal);
        settleOnCompletion(request, resolve, reject);

        request.send(file);
    });
}

type PendingStatusResult = { status: MediaclipPendingStatus; remaining?: number };

type PollOutcome = { outcome: 'done' | 'pending' | 'unreachable'; remaining?: number };

function pendingStatusUrl(session: MediaclipUploadSession, photoId: string, attempt: number): string {
    return `${session.photos_url}/photos/${encodeURIComponent(photoId)}/pendingStatus?attempt=${attempt}`;
}

/** Own controller so each check gets its own timeout, while a cancelled upload still aborts it. */
async function fetchPendingStatus(
    session: MediaclipUploadSession,
    photoId: string,
    attempt: number,
    signal: AbortSignal | undefined,
): Promise<PendingStatusResult> {
    const controller = new AbortController();
    const abortCheck = (): void => controller.abort();
    const timeout = setTimeout(abortCheck, STATUS_REQUEST_TIMEOUT_MS);
    signal?.addEventListener('abort', abortCheck, { once: true });

    try {
        const response = await fetch(pendingStatusUrl(session, photoId, attempt), {
            method: 'GET',
            headers: { Authorization: authorizationHeader(session), Accept: 'application/json' },
            signal: controller.signal,
        });

        if (!response.ok) {
            throw new DirectUploadError(`Mediaclip status check failed (${response.status})`, response.status);
        }

        return (await response.json()) as PendingStatusResult;
    } catch (error: unknown) {
        throwIfAborted(signal);
        throw error instanceof DirectUploadError ? error : new DirectUploadError('Mediaclip status check did not complete');
    } finally {
        clearTimeout(timeout);
        signal?.removeEventListener('abort', abortCheck);
    }
}

/** No status means a timeout, a network drop or an unreadable body; those, 429 and 5xx are worth another try. */
function isTransientStatusFailure(error: unknown): boolean {
    if (!(error instanceof DirectUploadError)) {
        return false;
    }

    return error.status === undefined || error.status === HTTP_TOO_MANY_REQUESTS || error.status >= FIRST_SERVER_ERROR_STATUS;
}

async function pollOnce(session: MediaclipUploadSession, photoId: string, attempt: number, signal: AbortSignal | undefined): Promise<PollOutcome> {
    let result: PendingStatusResult;

    try {
        result = await fetchPendingStatus(session, photoId, attempt, signal);
    } catch (error: unknown) {
        if (!isTransientStatusFailure(error)) {
            throw error;
        }

        return { outcome: 'unreachable' };
    }

    if (result.status === 'Error') {
        throw new DirectUploadError('Mediaclip could not process the photo', HTTP_UNPROCESSABLE_ENTITY);
    }

    return { outcome: result.status === 'Done' ? 'done' : 'pending', remaining: result.remaining };
}

/**
 * Waits in the browser so Mediaclip's processing never occupies a PHP worker. The first status
 * check goes out straight away: most photos are done by the time it returns.
 */
export async function waitForPhotoProcessing(
    session: MediaclipUploadSession,
    photo: MediaclipUploadedPhoto,
    fileSizeBytes: number,
    signal?: AbortSignal,
): Promise<void> {
    if (photo.ready) {
        return;
    }

    await pollUntilProcessed(session, photo, Date.now(), processingTimeoutMs(fileSizeBytes), signal);
}

async function pollUntilProcessed(
    session: MediaclipUploadSession,
    photo: MediaclipUploadedPhoto,
    startedAt: number,
    timeoutMs: number,
    signal: AbortSignal | undefined,
): Promise<void> {
    let consecutiveFailures = 0;

    for (let attempt = 1; Date.now() - startedAt <= timeoutMs; attempt++) {
        throwIfAborted(signal);

        const poll = await pollOnce(session, photo.photoId, attempt, signal);

        if (poll.outcome === 'done') {
            return;
        }

        consecutiveFailures = poll.outcome === 'unreachable' ? consecutiveFailures + 1 : 0;

        if (consecutiveFailures >= MAX_CONSECUTIVE_STATUS_FAILURES) {
            throw new DirectUploadError('Mediaclip status checks keep failing');
        }

        await abortableSleep(clampSleepMs(poll.remaining), signal);
    }

    throw new DirectUploadError('Timed out waiting for Mediaclip to process the photo', HTTP_REQUEST_TIMEOUT);
}
